/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react'
import { format } from 'date-fns'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import * as Yup from 'yup'

import { all } from 'store/Industry/Industry.selector'
import { all as allCertificate } from 'store/IndustryCertificate/industryCertificate.selector'

import { industryActions } from 'store/Industry'
import { industryCertificateActions } from 'store/IndustryCertificate'

import { UserStorageService } from 'services'
import { submitOnEnter } from 'utils/submitOnEnter'

import {
  INITIAL_STATE_FORM,
  INITIAL_STATE_FORM_ERROR,
  status,
  typesDate
} from 'constants/certifications.constants'

import { IndustryCertificateDetailsTable } from './IndustryCertificateDetailsTable'
import { Input } from 'components/Input'
import { Button } from 'components/Button'
import { InputDate } from 'components/InputDate'
import { Pagination } from 'components/Pagination'
import { InputSelect } from 'components/InputSelect'
import { RadioButton } from 'components/RadioButton'

import * as S from './styled'

const schema = Yup.object().shape({
  end: Yup.string()
    .nullable(true)
    .test('end', 'Data inválida', val => {
      if (val === 'Invalid Date') return false
      return true
    })
    .test('end', 'Informe a data', (val, props) => {
      if (props.parent.start && !val) return false
      return true
    })
    .default(undefined),
  start: Yup.string()
    .nullable(true)
    .test('start', 'Data inválida', val => {
      if (val === 'Invalid Date') return false
      return true
    })
    .test('start', 'Informe a data', (val, props) => {
      if (props.parent.end && !val) return false
      return true
    })
    .default(undefined),
  typeDate: Yup.string()
    .nullable(true)
    .test('typeDate', 'Informe o tipo de data', (val, props) => {
      if (props.parent.start && !val) return false
      if (props.parent.end && !val) return false
      return true
    })
    .default('dtEmitted'),
  acronym: Yup.string().optional(),
  status: Yup.string().default('all'),
  limit: Yup.number().default(10),
  page: Yup.number()
})

interface ParamTypes {
  id: string
}

export function IndustryCertificateDetails() {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const { id } = useParams<ParamTypes>()

  const { user } = UserStorageService.getUserData()

  const { industry }: any = useSelector(all)
  const { certificates }: any = useSelector(allCertificate)

  const [filter, setFilter] = useState({ ...INITIAL_STATE_FORM })
  const [errors, setErrors] = useState({ ...INITIAL_STATE_FORM_ERROR })

  function handleInput(value: string | Date, name: string) {
    setErrors({ ...errors, [name]: '' })
    setFilter({ ...filter, [name]: value })
  }

  function handleSelect(value: any, name: string) {
    setErrors({ ...errors, [name]: '' })
    if (value.value) setFilter({ ...filter, [name]: value.value })
    else setFilter({ ...filter, [name]: value })
  }

  function onFilterPaginate(params = {}) {
    dispatch(industryCertificateActions.fetchAll({ params, companyId: id }))
  }

  function clearFilter() {
    const params = {
      ...INITIAL_STATE_FORM,
      limit: 10,
      page: 0
    }
    setFilter(params)
    setErrors({ ...INITIAL_STATE_FORM_ERROR })
    dispatch(industryCertificateActions.fetchAll({ params, companyId: id }))
  }

  async function onFilter() {
    try {
      const params = await schema.validate(filter, {
        abortEarly: false,
        stripUnknown: true
      })

      if (params.start && params.end) {
        params.start = format(new Date(params.start), 'yyyy-MM-dd')
        params.end = format(new Date(params.end), 'yyyy-MM-dd')
      }

      dispatch(
        industryCertificateActions.fetchAll({
          params,
          companyId: id
        })
      )
    } catch (ex) {
      const pairs = ex.inner.map(({ path, message }) => [
        path,
        message.replace(`${path} `, '')
      ])
      setErrors(Object.fromEntries(pairs) as typeof INITIAL_STATE_FORM_ERROR)
    }
  }

  useEffect(() => {
    dispatch(industryActions.fetchOne(id))
    dispatch(
      industryCertificateActions.fetchAll({ params: filter, companyId: id })
    )
  }, [dispatch])

  return (
    <S.Wrapper container>
      <S.FullGrid item xs={12}>
        <S.GridInformations container>
          <S.BoxInformation item xs={12} sm={6} md={6}>
            <S.GridHeader>
              <S.GridTitle item container xs={12}>
                <S.BoxTitle>{t('indCertifications:industryData')}</S.BoxTitle>
              </S.GridTitle>
            </S.GridHeader>
            <S.BoxTextValue>
              <S.TextTitle>{t('indCertifications:industryName')}:</S.TextTitle>
              <S.TextValue>{industry.name}</S.TextValue>
            </S.BoxTextValue>

            <S.BoxTextValue>
              <S.TextTitle>{t('indCertifications:industrySif')}:</S.TextTitle>
              <S.TextValue>{industry.inspectionNum}</S.TextValue>
            </S.BoxTextValue>

            <S.BoxTextValue>
              <S.TextTitle>
                {t('indCertifications:industryLocalization')}:
              </S.TextTitle>
              <S.TextValue>
                {industry.city} / {industry.state}
              </S.TextValue>
            </S.BoxTextValue>
          </S.BoxInformation>
          <S.BoxInformation item xs={12} sm={6} md={6}>
            <S.BoxIndustryImage>
              <S.IndustryImage
                src={
                  industry.photos && industry.photos.length > 0
                    ? industry.photos[0]?.file?.src
                    : ''
                }
              />
            </S.BoxIndustryImage>
          </S.BoxInformation>
        </S.GridInformations>
      </S.FullGrid>

      <S.FullGrid item xs={12}>
        <S.GridHeader>
          <S.GridTitle item container xs={12}>
            <S.BoxTitle>{t('indCertifications:certifications')}</S.BoxTitle>
          </S.GridTitle>
        </S.GridHeader>

        <S.GridFilter container>
          <S.GridInput item xs={12} sm={6} md={3}>
            <InputDate
              error={Boolean(errors.start)}
              fullWidth
              helperText={errors.start}
              id="dtStart"
              minDate={new Date(2021, 0, 1)}
              label={t('common:initialDate')}
              name="dtStart"
              value={filter.start}
              onInput={value => handleInput(value, 'start')}
            />
          </S.GridInput>

          <S.GridInput item xs={12} sm={6} md={3}>
            <InputDate
              error={Boolean(errors.end)}
              fullWidth
              helperText={errors.end}
              id="dtFinal"
              label={t('common:finalDate')}
              name="dtFinal"
              value={filter.end}
              onInput={value => handleInput(value, 'end')}
            />
          </S.GridInput>

          <S.GridInput item xs={12} sm={6} md={4}>
            <RadioButton
              error={Boolean(errors.typeDate)}
              helperText={errors.typeDate}
              options={typesDate()}
              label=""
              onSelected={value => handleSelect(value, 'typeDate')}
              value={filter.typeDate}
            />
          </S.GridInput>
        </S.GridFilter>
        <S.GridFilter>
          <S.GridInput item xs={12} sm={6} md={3}>
            <Input
              error={Boolean(errors.acronym)}
              fullWidth
              helperText={errors.acronym}
              label={t('common:acronym')}
              onInput={value => handleInput(value, 'acronym')}
              onKeyPress={submitOnEnter(onFilter)}
              value={filter.acronym}
            />
          </S.GridInput>

          {user.tag === 'admin' && (
            <S.GridInput item xs={12} sm={6} md={3}>
              <InputSelect
                error={Boolean(errors.status)}
                fullWidth
                helperText={errors.status}
                label={t('common:status')}
                onSelected={value => handleSelect(value, 'status')}
                optionLabel="title"
                optionValue="value"
                options={status()}
                value={filter.status}
              />
            </S.GridInput>
          )}

          <S.GridButtons item xs={12} sm={12} md={user.tag === 'admin' ? 6 : 9}>
            <Button variant="default" onClick={clearFilter} size="medium">
              <p>{t('common:clean')}</p>
            </Button>
            <Button variant="primary" onClick={onFilter} size="medium">
              <p>{t('common:search')}</p>
            </Button>
          </S.GridButtons>
        </S.GridFilter>

        <S.BoxTable>
          <IndustryCertificateDetailsTable rows={certificates.items} />
          {certificates.pagination?.totalItems > 0 && (
            <Pagination
              filters={{
                ...filter,
                limit: certificates.pagination.limit,
                page: certificates.pagination.page
              }}
              limit={certificates.pagination.limit}
              page={certificates.pagination.page}
              totalItems={certificates.pagination.totalItems}
              setFilter={onFilterPaginate}
            />
          )}
        </S.BoxTable>
      </S.FullGrid>
    </S.Wrapper>
  )
}
